import React from 'react'

export type DataViewProps = {
  /** Fenced-block body: `src:`/`title:`/`kind:` lines, or inline JSON starting with `{`. */
  raw: string
}

type Config = { src?: string; title?: string; kind: 'settings' | 'json' }

function parseConfig(raw: string): { config: Config; inlineJson?: string } {
  const trimmed = raw.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return { config: { kind: 'settings' }, inlineJson: trimmed }
  }
  const config: Config = { kind: 'settings' }
  for (const line of trimmed.split('\n')) {
    const [key, ...rest] = line.split(':')
    const value = rest.join(':').trim()
    if (key.trim() === 'src') config.src = value
    else if (key.trim() === 'title') config.title = value
    else if (key.trim() === 'kind') config.kind = value === 'json' ? 'json' : 'settings'
  }
  return { config }
}

function formatDefault(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Table/Code toggle for real example data. Given a settings object it renders a summary table
 * (key, type, label, default, required) by default, with a "Code" view showing the full JSON.
 */
export default function DataView({ raw }: DataViewProps) {
  const { config, inlineJson } = React.useMemo(() => parseConfig(raw), [raw])
  const [view, setView] = React.useState<'table' | 'code'>(config.kind === 'json' ? 'code' : 'table')
  const [data, setData] = React.useState<any>(inlineJson ? safeParse(inlineJson) : null)
  const [error, setError] = React.useState<string>('')

  React.useEffect(() => {
    if (inlineJson || !config.src) return
    let cancelled = false
    fetch(config.src)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => !cancelled && setData(json))
      .catch((e) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [config.src, inlineJson])

  const entries = data && typeof data === 'object' && !Array.isArray(data) ? Object.entries<any>(data) : []
  const canTable = config.kind === 'settings' && entries.length > 0

  return (
    <div className="data-view">
      <div className="dv-toolbar">
        {config.title && <span className="dv-title">{config.title}</span>}
        <div className="dv-tabs" role="tablist">
          {canTable && (
            <button
              type="button"
              role="tab"
              aria-selected={view === 'table'}
              className={view === 'table' ? 'is-active' : ''}
              onClick={() => setView('table')}
            >
              Table
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={view === 'code'}
            className={view === 'code' ? 'is-active' : ''}
            onClick={() => setView('code')}
          >
            Code
          </button>
        </div>
      </div>

      {error && <p className="dv-error">Could not load example data ({error}).</p>}

      {!error && canTable && view === 'table' && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Type</th>
                <th>Label</th>
                <th>Default</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, def]) => (
                <tr key={key}>
                  <td><code>{key}</code></td>
                  <td>{def?.type ?? '-'}</td>
                  <td>{def?.label ?? '-'}</td>
                  <td>{formatDefault(def?.default)}</td>
                  <td>{def?.required ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && (view === 'code' || !canTable) && data && (
        <pre><code>{JSON.stringify(data, null, 2)}</code></pre>
      )}
    </div>
  )
}

function safeParse(s: string): any {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}
